import { useId, type JSX, type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { objectWorkspaceMessages as messages } from './ObjectWorkspace.messages';
import { ObjectWorkspaceHarnessHeader } from './ObjectWorkspaceHarnessHeader';
import type { ObjectWorkspaceObject } from './objectWorkspace.data';

export interface ObjectWorkspaceWorkTabDescriptor {
  id: string;
  title: string;
  kind?: 'heidi' | 'work';
  isThinking?: boolean;
  isDismissible?: boolean;
}

export interface ObjectWorkspaceRenderTabContentOptions {
  isActive: boolean;
}

export interface ObjectWorkspaceHarnessProps {
  object: ObjectWorkspaceObject;
  tabs: readonly ObjectWorkspaceWorkTabDescriptor[];
  activeTabId: string;
  onTabSelect: (tabId: string) => void;
  onAddTab: () => void;
  onDismissTab?: (tabId: string) => void;
  renderTabContent: (
    tab: ObjectWorkspaceWorkTabDescriptor,
    options: ObjectWorkspaceRenderTabContentOptions
  ) => ReactNode;
  /** Restores the hidden object pane. Omitted while the pane is showing. */
  onExpandObjectPane?: () => void;
  onOpenLastSession?: () => void;
  heidiTabIcon?: ReactNode;
}

/**
 * Package-safe tab chrome for app-owned object work. Chat state, routing, and
 * content stay with the app; this shell only renders controlled descriptors and
 * keeps every supplied tab panel mounted while switching visibility.
 */
export function ObjectWorkspaceHarness({
  object,
  tabs,
  activeTabId,
  onTabSelect,
  onAddTab,
  onDismissTab,
  renderTabContent,
  onExpandObjectPane,
  onOpenLastSession,
  heidiTabIcon,
}: ObjectWorkspaceHarnessProps): JSX.Element {
  const intl = useIntl();
  const tabRelationshipId = useId();
  const tabId = (id: string) => `${tabRelationshipId}-tab-${id}`;
  const panelId = (id: string) => `${tabRelationshipId}-panel-${id}`;

  return (
    <section
      aria-label={intl.formatMessage(messages.objectHarnessLabel, {
        name: object.name,
      })}
      className="flex size-full min-h-0 flex-col gap-2 bg-transparent"
      data-testid="object-workspace-harness"
    >
      <ObjectWorkspaceHarnessHeader
        tabs={tabs}
        activeTabId={activeTabId}
        objectName={object.name}
        getTabId={tabId}
        getPanelId={panelId}
        onTabSelect={onTabSelect}
        onAddTab={onAddTab}
        onDismissTab={onDismissTab}
        onExpandObjectPane={onExpandObjectPane}
        onOpenLastSession={onOpenLastSession}
        heidiTabIcon={heidiTabIcon}
      />

      <div
        className="relative min-h-0 flex-1 overflow-hidden bg-surface-page md:rounded-2xl"
        data-testid="object-workspace-thread-surface"
      >
        {tabs.map((tab) => {
          const isActive = tab.id === activeTabId;

          return (
            <div
              key={tab.id}
              id={panelId(tab.id)}
              aria-labelledby={tabId(tab.id)}
              className="size-full min-h-0"
              data-state={isActive ? 'active' : 'inactive'}
              hidden={!isActive}
              role="tabpanel"
              tabIndex={0}
            >
              {renderTabContent(tab, { isActive })}
            </div>
          );
        })}
      </div>
    </section>
  );
}
